
import { defineComponent, ref, computed, watch, nextTick, PropType } from 'vue'

export default defineComponent({
  props: {
    modelValue: {
      type: Number as PropType<number | null>,
      required: false
    },
    maxlength: {
      type: Number,
      default: 14
    },
    allowBlank: {
      type: Boolean,
      default: false
    },
    min: {
      type: Number,
      default: undefined
    }
  },
  emits: ['update:modelValue'],
  setup (props, { emit }) {
    const isFocused = ref(false)
    const inputRef = ref<HTMLInputElement | null>(null)

    const rawValue = ref(
      props.modelValue === null || props.modelValue === undefined
        ? ''
        : String(props.modelValue)
    )

    // 親 → 子の modelValue 更新
    watch(() => props.modelValue, (newVal) => {
      if (!isFocused.value) {
        rawValue.value = newVal === null || newVal === undefined ? '' : String(newVal)
      }
    })

    // 表示用の値
    //   入力中（フォーカス中）は「カンマ無し」の値をそのまま表示
    //   フォーカスが外れている場合は数値に変換してから3桁カンマ区切りで表示
    const displayValue = computed(() => {
      if (isFocused.value) {
        return rawValue.value
      }
      if (props.allowBlank && rawValue.value.trim() === '') {
        return ''
      }

      const num = parseInt(rawValue.value, 10)
      return isNaN(num) ? '0' : num.toLocaleString()
    })

    // 入力時
    const onInput = (e: Event) => {
      const target = e.target as HTMLInputElement
      let raw = filterValidCharacters(target.value)

      // maxlengthに合わせて桁数チェック。桁数オーバー分は削除。（マイナス記号は桁数に含めないので除外）
      const digitsOnly = raw.replace('-', '')
      if (digitsOnly.length > props.maxlength) {
        raw = (raw.startsWith('-') ? '-' : '') + digitsOnly.slice(0, props.maxlength)
      }

      let num = Number(raw)

      // min未満ならminに修正
      if (!isNaN(num) && props.min !== undefined) {
        if (num < props.min) {
          num = props.min
          raw = String(num)
        }
      }

      rawValue.value = raw

      if (props.allowBlank && raw.trim() === '') {
        emit('update:modelValue', null)
      } else {
        emit('update:modelValue', num)
      }
    }

    // フォーカス時
    const onFocus = () => {
      isFocused.value = true
      nextTick(() => {
        // nextTick で DOM 更新後に select() 呼び出し
        inputRef.value?.select()
      })
    }

    // フォーカス終了時
    const onBlur = () => {
      isFocused.value = false
      const sanitized = sanitizeNumber(rawValue.value)
      rawValue.value = sanitized === null ? '' : String(sanitized)
      emit('update:modelValue', sanitized)
    }

    // minが未設定 → マイナスも許可
    // minが設定済み → マイナス禁止
    const filterValidCharacters = (value: string): string => {
      const regex = props.min === undefined ? /^-?\d*/ : /^\d*/
      return value.match(regex)?.[0] || ''
    }

    // 数値化 + 空は 0
    const sanitizeNumber = (value: string): number | null => {
      const trimmed = value.trim()
      if (props.allowBlank && trimmed === '') return null

      const num = parseInt(trimmed, 10)
      return isNaN(num) ? 0 : num
    }

    return {
      inputRef,
      displayValue,
      onInput,
      onFocus,
      onBlur
    }
  }
})
